/**
 * HCIP source linking.
 *
 * Each of the eleven volumes of R. C. Majumdar's "History and Culture of the
 * Indian People" (Bharatiya Vidya Bhavan, 1951–1977) has a public-domain scan
 * on archive.org. The identifiers below come from pdfs/SOURCES.md and are the
 * stable URL slugs at the time of writing.
 *
 * URL patterns:
 *   - Item page:  https://archive.org/details/<id>
 *   - Specific page in inline reader:  https://archive.org/details/<id>/page/n<N>
 *
 * The "page" in archive.org's URL is the SCAN page (0-indexed from the front
 * cover), not the printed page number — they diverge by a small offset (front
 * matter, blank pages). We can refine the offset per volume later; for now we
 * link to the volume's item page and surface page/chapter in the visible
 * citation text, which is more honest than a wrong deep link.
 */

export interface HcipVolume {
  volume:     number          // 1–11
  title:      string          // canonical HCIP title
  identifier: string          // archive.org identifier
  startYear:  number
  endYear:    number
}

export const HCIP_VOLUMES: Record<number, HcipVolume> = {
  1:  { volume: 1,  title: 'The Vedic Age',                                              identifier: 'vedicage00bhar',         startYear: -3000, endYear: -600  },
  2:  { volume: 2,  title: 'The Age of Imperial Unity',                                  identifier: 'ageofimperialuni07bhar', startYear: -600,  endYear: 320   },
  3:  { volume: 3,  title: 'The Classical Age',                                          identifier: 'classicalage03bhar',     startYear: 320,   endYear: 750   },
  4:  { volume: 4,  title: 'The Age of Imperial Kanauj',                                 identifier: 'ageofimperialkan04bhar', startYear: 750,   endYear: 1000  },
  5:  { volume: 5,  title: 'The Struggle for Empire',                                    identifier: 'struggleforempir05bhar', startYear: 1000,  endYear: 1300  },
  6:  { volume: 6,  title: 'The Delhi Sultanate',                                        identifier: 'delhisultanate00bhar',   startYear: 1300,  endYear: 1526  },
  7:  { volume: 7,  title: 'The Mughul Empire',                                          identifier: 'mughulempire00bhar',     startYear: 1526,  endYear: 1707  },
  8:  { volume: 8,  title: 'The Maratha Supremacy',                                      identifier: 'marathasupremacy08bhar', startYear: 1707,  endYear: 1818  },
  9:  { volume: 9,  title: 'British Paramountcy and Indian Renaissance, Part 1',         identifier: 'britishparamount09bhar', startYear: 1818,  endYear: 1905  },
  10: { volume: 10, title: 'British Paramountcy and Indian Renaissance, Part 2',         identifier: 'britishparamount10bhar', startYear: 1818,  endYear: 1905  },
  11: { volume: 11, title: 'Struggle for Freedom',                                       identifier: 'struggleforfreed00bhar', startYear: 1905,  endYear: 1947  }
}

/** Combined 11-volume scan, useful as a generic fallback "see the source" link. */
export const HCIP_COMBINED_URL =
  'https://archive.org/details/the-history-and-culture-of-the-indian-people-11-vol.-set-by-r.-c.-majumdar-j.-n.'

/**
 * Build a stable archive.org URL for a HCIP volume.
 *
 * When a page string is provided we attempt to parse the first integer in it
 * (so "234–340" → 234, "p. 47" → 47) and append archive.org's `/page/n<N>`
 * deep-link to the inline PDF reader. archive.org auto-clamps out-of-range
 * page indices, so even if our offset is wrong the link still opens the
 * volume rather than 404'ing.
 *
 * Returns null if the volume isn't in our map (defensive — shouldn't happen
 * for any vol 1–11 in events.json).
 */
export function hcipUrl(volume: number, pages?: string): string | null {
  const vol = HCIP_VOLUMES[volume]
  if (!vol) return null
  const base = `https://archive.org/details/${vol.identifier}`
  if (!pages) return base
  const firstInt = pages.match(/-?\d+/)
  if (!firstInt) return base
  return `${base}/page/n${firstInt[0]}`
}
